import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { useAlert } from 'hooks/useAlert'
import { useTimedState } from 'hooks/useTimedState/useTimedState'
import { Check, Link } from 'lucide-react'
import { FormattedMessage, useIntl } from 'react-intl'

const MAX_FIELD_BYTES = 1024

export type C1CopyDataAsURLButtonProps<T extends Record<string, unknown>> = {
    disabled?: boolean
    data: T
    basePath: string
    fieldsToValidateMaxLength: Array<keyof T>
    queryParamKey?: string
}

/**
 * Takes data and turns it into a URL query parameter for sharing.
 * Validates field lengths and reports if they were too large.
 */
export const C1CopyDataAsURLButton = <T extends Record<string, unknown>>({
    disabled = false,
    data,
    basePath,
    fieldsToValidateMaxLength,
    queryParamKey = 'copyToken',
}: C1CopyDataAsURLButtonProps<T>): React.ReactNode => {
    const [isCopied, triggerIsCopied] = useTimedState()
    const intl = useIntl()

    const toast = useAlert()

    const copyFormToURL = async (): Promise<void> => {
        const serializedData = { ...data }

        for (const field of fieldsToValidateMaxLength) {
            const fieldValue = data[field]
            if (fieldValue && typeof fieldValue === 'string') {
                const fieldBytes = new TextEncoder().encode(fieldValue).length
                if (fieldBytes > MAX_FIELD_BYTES) {
                    toast(intl.formatMessage({
                        id: 'components_copy_button_field_too_long',
                        defaultMessage: 'Your {field} is too long to be included in the link, and was omitted.',
                    }, { field: String(field) }), 'error')

                    // Remove the field from the serialized data
                    delete serializedData[field]
                }
            }
        }

        try {
            const jsonString = JSON.stringify(serializedData)
            const utf8Bytes = new TextEncoder().encode(jsonString)
            const binString = Array.from(utf8Bytes, (byte) => String.fromCharCode(byte)).join('')
            const copyToken = btoa(binString)

            const shareableUrl = `${window.location.origin}${basePath}?${queryParamKey}=${encodeURIComponent(copyToken)}`

            await navigator.clipboard.writeText(shareableUrl)

            triggerIsCopied()
        } catch (error) {
            toast(intl.formatMessage({
                id: 'components_copy_button_error',
                defaultMessage: 'Failed to copy link to clipboard: {error}',
            }, { error: error instanceof Error ? error.message : 'Unknown error' }), 'error')
        }
    }

    return <Tooltip title={isCopied
        ? <Stack direction="row" spacing={1}>
            <C1Icon icon={Check} />
            <Typography>
                <FormattedMessage
                    id="component_copy_button_copied_tooltip"
                    defaultMessage="Link copied to clipboard"
                />
            </Typography>
        </Stack>
        : <FormattedMessage
            id="component_copy_button_tooltip"
            defaultMessage="Create a sharable link to this pre-filled form"
        />
    }
    >
        <Box>
            <C1Button startIcon={<C1Icon icon={Link} />} variant="text" onClick={copyFormToURL} disabled={disabled}>
                <FormattedMessage
                    id="component_copy_button_label"
                    defaultMessage="Create link"
                />
            </C1Button>
        </Box>
    </Tooltip>
}
